import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Clipboard,
  Download,
  RefreshCw,
  Search,
  Settings2,
  Terminal,
} from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDebounce } from '@/hooks/useDebounce';
import { cn } from '@/lib/utils';
import {
  usePostgresLogs,
  usePostgresProfilerSettings,
  usePostgresSlowQueries,
} from '../hooks/usePostgresProfiler';
import type {
  PostgresLogEntry,
  PostgresProfilerSortKey,
  PostgresSlowQuery,
  SortDirection,
} from '../types';

type SlowQueryColumnKey =
  | 'queryId'
  | 'calls'
  | 'meanExecTime'
  | 'totalExecTime'
  | 'maxExecTime'
  | 'sharedBlksRead'
  | 'tempBlksWritten'
  | 'query';

interface SlowQueryColumn {
  key: SlowQueryColumnKey;
  label: string;
  sortable?: PostgresProfilerSortKey;
  defaultVisible?: boolean;
  align?: 'left' | 'right';
  className?: string;
  getValue: (query: PostgresSlowQuery) => string;
  render?: (query: PostgresSlowQuery) => ReactNode;
}

const VISIBILITY_STORAGE_KEY = 'atek-postgres-profiler-column-visibility';

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatMs(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return 'N/A';
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${value.toFixed(1)}ms`;
}

function formatTimestamp(value: string): string {
  const parsed = new Date(value.replace(' ', 'T').replace(' UTC', 'Z'));
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function truncateQuery(value: string, maxLength = 220): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength).trim()}...`;
}

const SLOW_QUERY_COLUMNS: SlowQueryColumn[] = [
  {
    key: 'queryId',
    label: 'Query ID',
    defaultVisible: false,
    getValue: (query) => query.queryId || '',
    render: (query) => (
      <span className="font-mono text-xs text-muted-foreground">{query.queryId || 'N/A'}</span>
    ),
  },
  {
    key: 'calls',
    label: 'Calls',
    sortable: 'calls',
    align: 'right',
    getValue: (query) => String(query.calls),
    render: (query) => <span className="font-mono">{formatNumber(query.calls)}</span>,
  },
  {
    key: 'meanExecTime',
    label: 'Mean',
    sortable: 'meanExecTime',
    align: 'right',
    getValue: (query) => formatMs(query.meanExecTimeMs),
    render: (query) => <span className="font-mono">{formatMs(query.meanExecTimeMs)}</span>,
  },
  {
    key: 'totalExecTime',
    label: 'Total',
    sortable: 'totalExecTime',
    align: 'right',
    getValue: (query) => formatMs(query.totalExecTimeMs),
    render: (query) => <span className="font-mono">{formatMs(query.totalExecTimeMs)}</span>,
  },
  {
    key: 'maxExecTime',
    label: 'Max',
    sortable: 'maxExecTime',
    align: 'right',
    getValue: (query) => formatMs(query.maxExecTimeMs),
    render: (query) => <span className="font-mono">{formatMs(query.maxExecTimeMs)}</span>,
  },
  {
    key: 'sharedBlksRead',
    label: 'Reads',
    sortable: 'sharedBlksRead',
    align: 'right',
    getValue: (query) => String(query.sharedBlksRead),
    render: (query) => <span className="font-mono">{formatNumber(query.sharedBlksRead)}</span>,
  },
  {
    key: 'tempBlksWritten',
    label: 'Temp Writes',
    sortable: 'tempBlksWritten',
    align: 'right',
    defaultVisible: false,
    getValue: (query) => String(query.tempBlksWritten),
    render: (query) => <span className="font-mono">{formatNumber(query.tempBlksWritten)}</span>,
  },
  {
    key: 'query',
    label: 'Query',
    className: 'min-w-[34rem]',
    getValue: (query) => query.query,
    render: (query) => (
      <pre className="max-w-[52rem] whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700">
        {truncateQuery(query.query, 600)}
      </pre>
    ),
  },
];

const DEFAULT_COLUMN_VISIBILITY = SLOW_QUERY_COLUMNS.reduce((acc, column) => {
  acc[column.key] = column.defaultVisible !== false;
  return acc;
}, {} as Record<SlowQueryColumnKey, boolean>);

const EMPTY_COLUMN_FILTERS = SLOW_QUERY_COLUMNS.reduce((acc, column) => {
  acc[column.key] = '';
  return acc;
}, {} as Record<SlowQueryColumnKey, string>);

function loadColumnVisibility(): Record<SlowQueryColumnKey, boolean> {
  try {
    const stored = localStorage.getItem(VISIBILITY_STORAGE_KEY);
    if (!stored) return DEFAULT_COLUMN_VISIBILITY;
    return { ...DEFAULT_COLUMN_VISIBILITY, ...JSON.parse(stored) };
  } catch {
    return DEFAULT_COLUMN_VISIBILITY;
  }
}

function saveColumnVisibility(visibility: Record<SlowQueryColumnKey, boolean>) {
  localStorage.setItem(VISIBILITY_STORAGE_KEY, JSON.stringify(visibility));
}

function buildCsv(rows: string[][]): string {
  const escapeCell = (value: string) => `"${value.replace(/"/g, '""')}"`;
  return rows.map((row) => row.map(escapeCell).join(',')).join('\n');
}

function downloadCsv(filename: string, rows: string[][]) {
  const blob = new Blob([buildCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

async function copyRows(rows: string[][]) {
  await navigator.clipboard.writeText(rows.map((row) => row.join('\t')).join('\n'));
}

function SortIcon({
  columnSortKey,
  sortKey,
  sortDirection,
}: {
  columnSortKey?: PostgresProfilerSortKey;
  sortKey: PostgresProfilerSortKey;
  sortDirection: SortDirection;
}) {
  if (!columnSortKey || columnSortKey !== sortKey) return null;
  return sortDirection === 'asc'
    ? <ArrowUp className="ml-1 inline h-3.5 w-3.5" />
    : <ArrowDown className="ml-1 inline h-3.5 w-3.5" />;
}

function SettingsCard({
  title,
  value,
  tone = 'default',
}: {
  title: string;
  value: string;
  tone?: 'default' | 'success' | 'warning';
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-xs font-medium uppercase text-muted-foreground">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={cn(
          'break-words text-base font-semibold',
          tone === 'success' && 'text-emerald-700',
          tone === 'warning' && 'text-amber-700',
        )}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function SkeletonRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, index) => (
        <TableRow key={index}>
          {Array.from({ length: columns }).map((__, colIndex) => (
            <TableCell key={colIndex}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function buildSlowQueryRows(columns: SlowQueryColumn[], rows: PostgresSlowQuery[]): string[][] {
  return [
    columns.map((column) => column.label),
    ...rows.map((row) => columns.map((column) => column.getValue(row))),
  ];
}

function buildLogRows(entries: PostgresLogEntry[]): string[][] {
  return [
    ['Timestamp', 'Duration', 'Type', 'User', 'Database', 'Query'],
    ...entries.map((entry) => [
      entry.timestamp,
      formatMs(entry.durationMs),
      entry.type,
      entry.user || '',
      entry.database || '',
      entry.queryText || entry.raw,
    ]),
  ];
}

export function PostgresProfilerPage() {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageLength, setPageLength] = useState(25);
  const [sortKey, setSortKey] = useState<PostgresProfilerSortKey>('totalExecTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [logLimit, setLogLimit] = useState(50);
  const [columnVisibility, setColumnVisibility] = useState(loadColumnVisibility);
  const [columnFilters, setColumnFilters] = useState(() => ({ ...EMPTY_COLUMN_FILTERS }));

  const slowQueryFilters = {
    search: debouncedSearch || undefined,
    pageNumber,
    pageLength,
    sortKey,
    sortDirection,
  };

  const settingsQuery = usePostgresProfilerSettings();
  const slowQueriesQuery = usePostgresSlowQueries(slowQueryFilters);
  const logsQuery = usePostgresLogs({ limit: logLimit });

  const visibleColumns = useMemo(
    () => SLOW_QUERY_COLUMNS.filter((column) => columnVisibility[column.key] !== false),
    [columnVisibility],
  );

  const filteredQueries = useMemo(() => {
    const rows = slowQueriesQuery.data?.queries || [];
    return rows.filter((row) => {
      for (const column of SLOW_QUERY_COLUMNS) {
        const filter = columnFilters[column.key]?.trim().toLowerCase();
        if (!filter) continue;
        if (!column.getValue(row).toLowerCase().includes(filter)) return false;
      }
      return true;
    });
  }, [columnFilters, slowQueriesQuery.data?.queries]);

  const settings = settingsQuery.data?.settings || {};
  const sharedPreload = settings.shared_preload_libraries || '';
  const pgStatStatementsEnabled = sharedPreload.includes('pg_stat_statements');
  const autoExplainEnabled = sharedPreload.includes('auto_explain');
  const totalPages = Math.max(Math.ceil((slowQueriesQuery.data?.total || 0) / pageLength), 1);

  function toggleColumn(key: SlowQueryColumnKey) {
    const next = { ...columnVisibility, [key]: !columnVisibility[key] };
    setColumnVisibility(next);
    saveColumnVisibility(next);
  }

  function handleSort(column: SlowQueryColumn) {
    if (!column.sortable) return;
    if (sortKey === column.sortable) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(column.sortable);
      setSortDirection('desc');
    }
    setPageNumber(0);
  }

  function clearFilters() {
    setSearch('');
    setColumnFilters({ ...EMPTY_COLUMN_FILTERS });
    setPageNumber(0);
  }

  function refresh() {
    settingsQuery.refetch();
    slowQueriesQuery.refetch();
    logsQuery.refetch();
  }

  async function copySlowQueries() {
    try {
      await copyRows(buildSlowQueryRows(visibleColumns, filteredQueries));
      toast.success('Copied slow queries');
    } catch {
      toast.error('Could not copy slow queries');
    }
  }

  async function copyLogs() {
    try {
      await copyRows(buildLogRows(logsQuery.data?.entries || []));
      toast.success('Copied log entries');
    } catch {
      toast.error('Could not copy log entries');
    }
  }

  const hasError = settingsQuery.error || slowQueriesQuery.error || logsQuery.error;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-slate-900 p-2 text-white">
                <Terminal className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold tracking-normal text-slate-950">
                  PostgreSQL Profiler
                </h1>
                <p className="text-sm text-muted-foreground">
                  pg_stat_statements, slow statements, and auto_explain plans.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={pgStatStatementsEnabled ? 'success' : 'warning'}>
              pg_stat_statements {pgStatStatementsEnabled ? 'on' : 'off'}
            </Badge>
            <Badge variant={autoExplainEnabled ? 'success' : 'warning'}>
              auto_explain {autoExplainEnabled ? 'on' : 'off'}
            </Badge>
            <Button variant="outline" size="sm" onClick={refresh} disabled={slowQueriesQuery.isFetching || logsQuery.isFetching}>
              <RefreshCw className={cn('h-4 w-4', (slowQueriesQuery.isFetching || logsQuery.isFetching) && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-[1600px] space-y-5 px-6 py-6">
        {hasError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Profiler data could not be loaded</AlertTitle>
            <AlertDescription>
              {hasError instanceof Error ? hasError.message : 'The profiler API returned an error.'}
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          <SettingsCard
            title="Slow Threshold"
            value={settings.log_min_duration_statement ? `${settings.log_min_duration_statement}ms` : 'N/A'}
            tone={settings.log_min_duration_statement ? 'success' : 'warning'}
          />
          <SettingsCard
            title="Lock Wait Logs"
            value={settings.log_lock_waits === 'on' ? 'On' : 'Off'}
            tone={settings.log_lock_waits === 'on' ? 'success' : 'warning'}
          />
          <SettingsCard
            title="Tracked Statements"
            value={settings['pg_stat_statements.track'] || 'N/A'}
            tone={settings['pg_stat_statements.track'] === 'all' ? 'success' : 'default'}
          />
          <SettingsCard
            title="Log Path"
            value={settingsQuery.data?.logPath || 'N/A'}
          />
        </div>

        <Tabs defaultValue="slow-queries" className="space-y-4">
          <TabsList>
            <TabsTrigger value="slow-queries">Slow Queries</TabsTrigger>
            <TabsTrigger value="logs">Recent Logs</TabsTrigger>
          </TabsList>

          <TabsContent value="slow-queries" className="space-y-4">
            <div className="flex flex-col gap-3 rounded border border-slate-200 bg-white p-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative min-w-0 flex-1 sm:max-w-md">
                  <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPageNumber(0);
                    }}
                    className="pl-9"
                    placeholder="Search SQL text"
                  />
                </div>
                <Select
                  value={String(pageLength)}
                  onValueChange={(value) => {
                    setPageLength(Number(value));
                    setPageNumber(0);
                  }}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 rows</SelectItem>
                    <SelectItem value="25">25 rows</SelectItem>
                    <SelectItem value="50">50 rows</SelectItem>
                    <SelectItem value="100">100 rows</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear Filters
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Settings2 className="h-4 w-4" />
                      Columns
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {SLOW_QUERY_COLUMNS.map((column) => (
                      <DropdownMenuCheckboxItem
                        key={column.key}
                        checked={columnVisibility[column.key] !== false}
                        onCheckedChange={() => toggleColumn(column.key)}
                      >
                        {column.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="outline" size="sm" onClick={copySlowQueries}>
                  <Clipboard className="h-4 w-4" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCsv('postgres-slow-queries.csv', buildSlowQueryRows(visibleColumns, filteredQueries))}
                >
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    {visibleColumns.map((column) => (
                      <TableHead
                        key={column.key}
                        className={cn(
                          column.align === 'right' && 'text-right',
                          column.sortable && 'cursor-pointer select-none hover:bg-slate-50',
                          column.className,
                        )}
                        onClick={() => handleSort(column)}
                      >
                        {column.label}
                        <SortIcon columnSortKey={column.sortable} sortKey={sortKey} sortDirection={sortDirection} />
                      </TableHead>
                    ))}
                  </TableRow>
                  <TableRow className="hover:bg-transparent">
                    {visibleColumns.map((column) => (
                      <TableHead key={column.key} className={column.className}>
                        <Input
                          value={columnFilters[column.key] || ''}
                          onChange={(event) => setColumnFilters({
                            ...columnFilters,
                            [column.key]: event.target.value,
                          })}
                          className="h-8 bg-white text-xs"
                          placeholder={`Filter ${column.label}`}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {slowQueriesQuery.isLoading ? (
                    <SkeletonRows columns={visibleColumns.length} />
                  ) : filteredQueries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={visibleColumns.length} className="h-28 text-center text-sm text-muted-foreground">
                        No slow query stats matched the current filters.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredQueries.map((query) => (
                      <TableRow key={query.queryId || query.query}>
                        {visibleColumns.map((column) => (
                          <TableCell
                            key={column.key}
                            className={cn(
                              column.align === 'right' && 'text-right',
                              column.className,
                            )}
                          >
                            {column.render ? column.render(query) : column.getValue(query)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="flex flex-col gap-3 text-sm text-muted-foreground sm:flex-row sm:items-center sm:justify-between">
              <div>
                Showing {filteredQueries.length} of {formatNumber(slowQueriesQuery.data?.total || 0)} tracked statements
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber === 0}
                  onClick={() => setPageNumber((current) => Math.max(current - 1, 0))}
                >
                  Previous
                </Button>
                <span className="min-w-[7rem] text-center">
                  Page {pageNumber + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber + 1 >= totalPages}
                  onClick={() => setPageNumber((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            <div className="flex flex-col gap-3 rounded border border-slate-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Reading {settingsQuery.data?.logPath || 'PostgreSQL log'} from the server tail.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={String(logLimit)} onValueChange={(value) => setLogLimit(Number(value))}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25 entries</SelectItem>
                    <SelectItem value="50">50 entries</SelectItem>
                    <SelectItem value="100">100 entries</SelectItem>
                    <SelectItem value="200">200 entries</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={copyLogs}>
                  <Clipboard className="h-4 w-4" />
                  Copy
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadCsv('postgres-slow-logs.csv', buildLogRows(logsQuery.data?.entries || []))}
                >
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
              </div>
            </div>

            <div className="overflow-hidden rounded border border-slate-200 bg-white">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[14rem]">Time</TableHead>
                    <TableHead className="w-[7rem] text-right">Duration</TableHead>
                    <TableHead className="w-[7rem]">Type</TableHead>
                    <TableHead className="w-[11rem]">User/DB</TableHead>
                    <TableHead>Entry</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logsQuery.isLoading ? (
                    <SkeletonRows columns={5} />
                  ) : (logsQuery.data?.entries || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-28 text-center text-sm text-muted-foreground">
                        No recent slow log entries were found in the current log tail.
                      </TableCell>
                    </TableRow>
                  ) : (
                    logsQuery.data?.entries.map((entry) => (
                      <TableRow key={`${entry.timestamp}-${entry.pid}-${entry.type}-${entry.raw.slice(0, 24)}`}>
                        <TableCell className="font-mono text-xs text-slate-600">
                          {formatTimestamp(entry.timestamp)}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatMs(entry.durationMs)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.type === 'plan' ? 'info' : 'secondary'}>
                            {entry.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-slate-600">
                          <div>{entry.user || 'N/A'}</div>
                          <div className="text-muted-foreground">{entry.database || 'N/A'}</div>
                        </TableCell>
                        <TableCell>
                          <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-slate-700">
                            {truncateQuery(entry.queryText || entry.raw, 700)}
                          </pre>
                          <details className="mt-2 text-xs text-muted-foreground">
                            <summary className="cursor-pointer">Raw log entry</summary>
                            <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-slate-950 p-3 font-mono text-[11px] leading-5 text-slate-100">
                              {entry.raw}
                            </pre>
                          </details>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
